import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlayCircle, Plus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth, useCan } from "@/context/AuthContext";
import { addDays, todayIso } from "@/lib/dates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateTimeInput24 } from "@/components/TimeInput24";

const fmtLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
const shiftEnd = (newStart, f) => {
  const dur = Math.max(new Date(f.end) - new Date(f.start), 3600000);
  return fmtLocal(new Date(new Date(newStart).getTime() + dur));
};

const NewBooking = ({ vehicles, onClose, onSaved }) => {
  const [f, setF] = useState({ vehicle_id: "", start: `${todayIso()}T07:00`, end: `${todayIso()}T13:00`, purpose: "" });
  const save = async () => {
    try { await api.post("/bookings", f); toast.success("Vehicle booked"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="booking-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Book a vehicle</DialogTitle></DialogHeader>
        <div className="space-y-1.5"><Label>Vehicle</Label>
          <Select value={f.vehicle_id || undefined} onValueChange={(v) => setF({ ...f, vehicle_id: v })}><SelectTrigger data-testid="booking-vehicle-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{vehicles.filter((v) => v.status !== "off_road").map((v) => <SelectItem key={v.id} value={v.id}>{v.name} ({v.type === "car" ? "car" : "bike"})</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>From</Label><DateTimeInput24 value={f.start} onChange={(v) => setF({ ...f, start: v, end: v >= f.end ? shiftEnd(v, f) : f.end })} testid="booking-start" /></div>
          <div className="space-y-1.5"><Label>To</Label><DateTimeInput24 value={f.end} onChange={(v) => setF({ ...f, end: v })} testid="booking-end" /></div>
        </div>
        <div className="space-y-1.5"><Label>Purpose</Label><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="booking-purpose" /></div>
        <button disabled={!f.vehicle_id} onClick={save} data-testid="booking-save-btn" className="w-full rounded-full bg-primary py-3 text-sm font-bold text-primary-foreground disabled:opacity-40">Book</button>
      </DialogContent>
    </Dialog>
  );
};

export default function Bookings() {
  const { user } = useAuth();
  const can = useCan();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/bookings", { params: { start: `${todayIso()}T00:00`, end: `${addDays(todayIso(), 30)}T23:59` } }).then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/bikes").then((r) => setVehicles(r.data)); }, []);
  const cancel = async (b) => {
    if (!window.confirm(`Cancel booking of ${b.vehicle_name}?`)) return;
    try { await api.delete(`/bookings/${b.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = items.filter((b) => filter === "all" || (filter === "mine" ? b.user_id === user.id : b.vehicle_type === filter));
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Vehicles</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Bookings</h1>
        </div>
        {can("bookings", 2) && <button onClick={() => setOpen(true)} data-testid="new-booking-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"><Plus className="h-4 w-4" /> Book vehicle</button>}
      </div>
      <div className="flex gap-2">
        {[["all", "All"], ["mine", "Mine"], ["bike", "Bikes"], ["car", "Cars"]].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} data-testid={`booking-filter-${k}`} className={`rounded-full border px-4 py-1.5 text-sm ${filter === k ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>)}
      </div>
      <section className="overflow-hidden rounded-2xl border bg-card" data-testid="bookings-list">
        {shown.length === 0 && <p className="px-5 py-8 text-sm text-muted-foreground">No bookings in the next 30 days.</p>}
        {shown.map((b) => (
          <div key={b.id} data-testid={`booking-row-${b.id}`} className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3 last:border-0">
            <div>
              <div className="font-semibold">{b.vehicle_name} · {b.user_name}</div>
              <div className="font-mono text-xs text-muted-foreground">{b.start.replace("T", " ")} → {b.end.slice(11)}{b.purpose ? ` · ${b.purpose}` : ""}</div>
            </div>
            <div className="flex gap-2">
              {b.user_id === user.id && b.start.slice(0, 10) === todayIso() && <button onClick={() => nav(`/bike-check?bike=${b.vehicle_id}`)} data-testid={`booking-check-${b.id}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground"><PlayCircle className="h-3.5 w-3.5" /> Pre-ride check</button>}
              {(b.user_id === user.id || can("bookings", 3)) && <button onClick={() => cancel(b)} data-testid={`booking-cancel-${b.id}`} className="rounded-full border px-3 py-1.5 text-xs text-red-300"><X className="mr-1 inline h-3 w-3" />Cancel</button>}
            </div>
          </div>
        ))}
      </section>
      {open && <NewBooking vehicles={vehicles} onClose={() => setOpen(false)} onSaved={load} />}
    </div>
  );
}
